import { z } from 'zod';

export const SignupFieldEntrySchema = z
  .object({
    fieldId: z.string().uuid(),
    value: z.union([
      z.object({ text: z.string().max(10000) }).strict(),
      z.object({ number: z.number().finite() }).strict(),
      z.object({ date: z.string() }).strict(),
      z.object({ select: z.string().max(1000) }).strict(),
      z.object({ boolean: z.boolean() }).strict(),
    ]),
  })
  .strict();
export type SignupFieldEntry = z.infer<typeof SignupFieldEntrySchema>;
export interface SignupCustomField {
  id: string;
  name: string;
  label: string;
  field_type:
    | 'text'
    | 'number'
    | 'date'
    | 'select'
    | 'boolean'
    | 'file'
    | 'tour';
  description: string | null;
  options: string[];
  is_required: boolean;
  display_order: number;
  for_parent: boolean;
  for_child: boolean;
  is_date_of_birth: boolean;
  min_value: number | null;
  max_value: number | null;
}

export function validateSignupFieldEntries(
  fields: SignupCustomField[],
  entries: SignupFieldEntry[],
  target: 'parent' | 'child',
) {
  const errors: Array<{ fieldId: string; message: string }> = [];
  const applicable = fields.filter((field) =>
    target === 'parent' ? field.for_parent : field.for_child,
  );
  const seen = new Set<string>();
  for (const entry of entries) {
    if (
      seen.has(entry.fieldId) ||
      !applicable.some((field) => field.id === entry.fieldId)
    ) {
      errors.push({ fieldId: entry.fieldId, message: 'Invalid signup field' });
    }
    seen.add(entry.fieldId);
  }
  for (const field of applicable) {
    const value = entries.find((entry) => entry.fieldId === field.id)?.value;
    const empty =
      !value ||
      ('text' in value && !value.text.trim()) ||
      ('date' in value && !value.date) ||
      ('select' in value && !value.select);
    let invalid = false;
    if (empty) {
      if (field.is_required)
        errors.push({
          fieldId: field.id,
          message: `${field.label} is required`,
        });
      continue;
    }
    switch (field.field_type) {
      case 'text':
        invalid = !('text' in value);
        break;
      case 'number':
        invalid =
          !('number' in value) ||
          !Number.isFinite(value.number) ||
          (field.min_value !== null && value.number < field.min_value) ||
          (field.max_value !== null && value.number > field.max_value);
        break;
      case 'boolean':
        invalid = !('boolean' in value);
        break;
      case 'select':
        invalid = !('select' in value) || !field.options.includes(value.select);
        break;
      case 'date': {
        const date = 'date' in value ? value.date : '';
        const parsed = new Date(`${date}T00:00:00Z`);
        invalid =
          !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
          date.startsWith('0000-') ||
          !Number.isFinite(parsed.getTime()) ||
          parsed.toISOString().slice(0, 10) !== date ||
          (field.is_date_of_birth &&
            date > new Date().toISOString().slice(0, 10));
        break;
      }
      default:
        invalid = true;
    }
    if (invalid)
      errors.push({
        fieldId: field.id,
        message: `Enter a valid ${field.label}`,
      });
  }
  return errors;
}
